import { createContext, useContext, useState, useCallback, useEffect } from "react";
import { apiClient } from "../services/api";
import { notificationStream } from "../services/notifications";
import { chatSse, doppelgangerSse, widgetSse } from "../services/sse";
import {
  AUTH_EXPIRED_EVENT,
  clearSensitiveSessionStorage,
  getTokenExpirationMs,
  isTokenExpired,
} from "../services/authSession";

const API_BASE = import.meta.env.VITE_API_URL || "/api";
const DIM0_BASE = (import.meta.env.VITE_DIM0_URL || "http://192.168.1.218:3000").replace(/\/$/, "");
const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    const saved = localStorage.getItem("anna_user");
    return saved ? JSON.parse(saved) : null;
  });
  const [token, setToken] = useState(localStorage.getItem("anna_token"));
  const [userStatus, setUserStatus] = useState(
    () => localStorage.getItem("anna_user_status") || null
  );
  const [loading, setLoading] = useState(true);

  const clearAuth = useCallback(() => {
    chatSse.stop();
    doppelgangerSse.stop();
    widgetSse.stop();
    notificationStream.disconnect();
    setToken(null);
    setUser(null);
    setUserStatus(null);
    clearSensitiveSessionStorage();
  }, []);

  // On mount, verify existing token
  useEffect(() => {
    if (token) {
      if (isTokenExpired(token)) {
        clearAuth();
        setLoading(false);
        return;
      }
      apiClient
        .post("/auth", { token })
        .then((data) => {
          if (data.valid) {
            setUser(data.user);
            const status = data.user?.status || "pending";
            setUserStatus(status);
            localStorage.setItem("anna_user_status", status);
          } else {
            clearAuth();
          }
        })
        .catch(() => clearAuth())
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, [token, clearAuth]);

  useEffect(() => {
    if (!token) return;

    const expiresAt = getTokenExpirationMs(token);
    if (expiresAt === null) {
      clearAuth();
      return;
    }

    const expireIfNeeded = () => {
      if (isTokenExpired(token)) clearAuth();
    };
    const timeoutId = window.setTimeout(expireIfNeeded, Math.max(0, expiresAt - Date.now() + 50));
    const handleVisibility = () => {
      if (document.visibilityState === "visible") expireIfNeeded();
    };
    const handleStorage = (event) => {
      if (event.key === "anna_token" && (!event.newValue || isTokenExpired(event.newValue))) {
        clearAuth();
      }
    };
    const handleExpired = () => clearAuth();

    window.addEventListener("focus", expireIfNeeded);
    window.addEventListener("storage", handleStorage);
    window.addEventListener(AUTH_EXPIRED_EVENT, handleExpired);
    document.addEventListener("visibilitychange", handleVisibility);

    return () => {
      window.clearTimeout(timeoutId);
      window.removeEventListener("focus", expireIfNeeded);
      window.removeEventListener("storage", handleStorage);
      window.removeEventListener(AUTH_EXPIRED_EVENT, handleExpired);
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, [token, clearAuth]);

  const login = useCallback(() => {
    // Redirect to API Gateway /auth which 302s to Google
    const callbackUrl = `${window.location.origin}/auth/callback`;
    window.location.href = `${API_BASE}/auth?redirect_uri=${encodeURIComponent(callbackUrl)}`;
  }, []);

  const openDim0 = useCallback((returnTo = "/") => {
    if (!token) {
      throw new Error("You must be signed in to open Dim0.");
    }

    const action = `${DIM0_BASE}/dim0-auth/browser/login`;
    const targetName = "anna-dim0";
    const popup = window.open("about:blank", targetName);
    const target = popup ? targetName : "_self";
    const form = document.createElement("form");
    form.method = "POST";
    form.action = action;
    form.target = target;
    form.style.display = "none";

    const tokenInput = document.createElement("input");
    tokenInput.type = "hidden";
    tokenInput.name = "token";
    tokenInput.value = token;
    form.appendChild(tokenInput);

    const returnToInput = document.createElement("input");
    returnToInput.type = "hidden";
    returnToInput.name = "return_to";
    returnToInput.value = returnTo;
    form.appendChild(returnToInput);

    document.body.appendChild(form);
    form.submit();
    form.remove();
  }, [token]);

  const handleCallback = useCallback(async (code) => {
    // SPA flow: frontend received code from Google, exchange it via backend
    const redirectUri = `${window.location.origin}/auth/callback`;
    const response = await apiClient.post("/auth/callback", {
      code,
      redirectUri,
    });
    const { token: newToken, user: userData } = response;
    const status = userData?.status || "pending";
    setToken(newToken);
    setUser(userData);
    setUserStatus(status);
    localStorage.setItem("anna_token", newToken);
    localStorage.setItem("anna_user", JSON.stringify(userData));
    localStorage.setItem("anna_user_status", status);
    return response;
  }, []);

  const logout = useCallback(() => {
    clearAuth();
  }, [clearAuth]);

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        isAuthenticated: Boolean(token),
        isApproved: userStatus === "approved",
        userStatus,
        loading,
        login,
        logout,
        handleCallback,
        openDim0,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
